import { resolveFormTenantId } from "../../utils/tenantContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { getStandardizedSignatures } from '../../utils/formHelpers';
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { request } from "../../utils/api";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import { confidentialityAgreementSchema } from "./application.validation";
import CloseButton from "../../components/Application/CloseButton";
import ApplicationFormFooter from "./ApplicationFormFooter";
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import { getLocalSystemDate, parseIsoDateToMDYUS } from "../../utils/dateUtils";
import { buildPayloadWithIsoDates } from "../../utils/applicationFormPayload";

const CONFIDENTIALITY_ISO_DATE_FIELDS = [
  "agreementDate",
  "applicantSignedAt",
  "reviewedBySignedAt",
];

const ConfidentialityAgreement = ({ positionName, onClose }) => {
  const [agreementId, setAgreementId] = useState(null);


  const methods = useForm({
    resolver: zodResolver(confidentialityAgreementSchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      employeeName: "",
      agreementDate: parseIsoDateToMDYUS(getLocalSystemDate()),
      applicantSignature: "",
      applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
      applicantSignedByUserId: 1,
      positionAppliedFor: positionName || "",
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: "",
      applicantStatementAccepted: false
    },
    mode: "onChange"
  });

  const { handleSubmit, setValue, watch, reset } = methods;
  const [currentStatus, setCurrentStatus] = useState("");

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      const payload = buildPayloadWithIsoDates(
        data,
        additionalData,
        CONFIDENTIALITY_ISO_DATE_FIELDS,
        { flag: actualFlag }
      );

      // Handle To Be Corrected comments
      const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      const method = agreementId ? 'PUT' : 'POST';
      const url = agreementId ? `/confidentiality-agreements/${agreementId}` : '/confidentiality-agreements';

      const response = await request(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (response.status === 1) {
        toast.success(response.message || "Confidentiality Agreement processed successfully!");
        closeForm()
      } else {
        toast.error(response.message || "Failed to process form!");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(`Error: ${error.message}`);
    }
  };

  const {
    handleStatusAction,
    handleSave,
    targetUserId,
    user: currentUserData,
    isAdmin,
    isReadOnly,
    closeForm,
    actualFromId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    handleSignatureApply,
    signerName
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "employeeName",
    applicantSignatureDateField: "applicantSignedAt",
    applicantSignatureField: "applicantSignature",
    adminSignatureField: "reviewedBySignature",
    adminSignatureDateField: "reviewedBySignedAt",
    onClose,
    onSubmit,
    currentStatus
  });

  useEffect(() => {
    if (positionName) {
      setValue("positionAppliedFor", positionName);
    }
  }, [positionName, setValue]);

  useEffect(() => {
    const fetchConfidentialityAgreement = async () => {
      // Must wait for currentUserData
      if (!currentUserData) return;
      if (isAdmin && !targetUserId) return;

      try {
        if (!actualFromId && !targetUserId && !agreementId) {
          if (currentUserData && !isAdmin) {
            methods.reset({
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              employeeName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
              agreementDate: parseIsoDateToMDYUS(getLocalSystemDate()),
              positionAppliedFor: positionName || "",
              applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
            });
          }
          return;
        }

        let response;
        if (isAdmin) {
          if (actualFromId) {
            response = await request(`/confidentiality-agreements/${actualFromId}?userId=${targetUserId}`);
          } else {
            response = await request(`/confidentiality-agreements?userId=${targetUserId}`);
          }
        } else {
          response = await request(`/confidentiality-agreements/${actualFromId || targetUserId}`);
        }

        let data = null;
        if (response.status === 1) {
          if (Array.isArray(response.result)) {
            if (response.result.length > 0) data = response.result[0];
          } else if (response.result?.agreement) {
            data = response.result.agreement;
          } else if (response.result?.items && Array.isArray(response.result.items)) {
            data = response.result.items.find(item => item.userId === Number.parseInt(targetUserId));
          }
        }

        if (data) {
          setAgreementId(data.id);
          setCurrentStatus(data.status || "");
          reset({
            ...data,
            employeeName: data.employeeName || data.applicantName || "",
            agreementDate: parseIsoDateToMDYUS(data.agreementDate) || getLocalSystemDate(),
            ...getStandardizedSignatures(data, isAdmin),
            applicantSignedAt: parseIsoDateToMDYUS(data.applicantSignedAt || data.employeeSignatureDate || data.empSignedAt || getLocalSystemDate()),
            reviewedBySignedAt: parseIsoDateToMDYUS(data.reviewedBySignedAt || data.adminSignatureDate || data.reviewerSignatureDate),
            applicantStatementAccepted: data.applicantStatementAccepted || false
          });
        }
      } catch (error) {
        console.error("Error fetching confidentiality agreement:", error);
      }
    };
    fetchConfidentialityAgreement();
  }, [targetUserId, reset, currentUserData, isAdmin, methods, actualFromId, agreementId, positionName]);

  return (
    <>
      <CloseButton onClose={closeForm} />
      <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-2xl my-8">
        <EvaluationFormHeader title="Confidentiality Agreement" />
        <div className="p-8 ">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <CorrectionRequestAlert status={currentStatus} comments={methods.watch("comments")} />
              <fieldset disabled={isReadOnly} className="space-y-6">
                <div className="mb-6 space-y-4 text-sm leading-relaxed text-gray-800">
                  <p>
                    I agree to the following terms as an employee of this Agency.
                  </p>
                  <p>
                    I understand that during my employment with this Agency, I will
                    have access to confidential patient/family, agency, and
                    personnel information. I agree that all patient/family
                    information must be held in strict confidence and used only as
                    necessary in performing my job responsibilities. I will not
                    communicate client information to other clients or to anyone not
                    involved in their care, including family members,
                    non-professional personnel, other organizations, the news media,
                    or the general public. I will not communicate negatively about
                    this Agency or its employees. Any communication with clients
                    requires prior written consent from the client or their legal
                    guardian. I understand that a breach of this agreement will
                    result in termination and possible civil actions.
                  </p>
                  <p>
                    I further agree to abide by this Agency's Health Insurance
                    Portability and Accountability Act Policies (HIPAA).
                  </p>
                </div>

                <ApplicationFormFooter
                  page={1}
                  totalPages={1}
                  methods={methods}
                  onSave={handleSave}
                  onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
                  onStatusUpdate={handleStatusAction}
                  isSignatureModalOpen={isSignatureModalOpen}
                  openSignatureModal={openSignatureModal}
                  closeSignatureModal={closeSignatureModal}
                  handleSignatureApply={handleSignatureApply}
                  signerName={signerName}
                  applicantName={watch("employeeName") || ""}
                  applicantSignature={watch("applicantSignature")}
                  applicantSignedAt={watch("applicantSignedAt")}
                  reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
                  reviewedBySignature={watch("reviewedBySignature")}
                  reviewedBySignedAt={watch("reviewedBySignedAt")}
                  submitLabel="Submit & Print"
                  isReadOnly={isReadOnly}
                  onClose={closeForm}
                  currentStatus={currentStatus}
                />

              </fieldset>
            </form>


          </FormProvider>
        </div>

      </div>
    </>
  );
};

export default ConfidentialityAgreement;
